import { toJS } from "mobx";

export default class TeamService {
    constructor(state, props, selectionStore, submitData, players) {
        this.state = state;
        this.props = props;
        this.selectionStore = selectionStore;
        this.submitData = submitData;
        this.players = players;
    }

    findSelectedItem(id) {
        return this.selectionStore.items.find((item) => item.id === id);
    }

    updatePlayerProp(id, prop, value) {
        const selectedItem = this.findSelectedItem(id);
        if (!selectedItem) return;

        const previousValue = selectedItem[prop];

        if (!(typeof value === "boolean") && prop !== "position") {
            selectedItem[prop] =
                value.length > 2 && !prop.includes("cost")
                    ? selectedItem[prop]
                    : value;

            selectedItem["total"] = this.calculateUmpireCost(id);
        } else {
            selectedItem[prop] = value === "" ? false : value;
        }

        this.selectionStore.items = [...this.selectionStore.items];
        return previousValue;
    }

    updatePositions(
        previousPosition,
        newPosition,
        allPositions,
        disabledPositions
    ) {
        let newDisabledPositions = [...disabledPositions];
        const positions = toJS(allPositions);

        const selectedPosition = positions.find(
            (position) => position.id === newPosition
        );
        const previousSelectedPosition = positions.find(
            (position) => position.id === previousPosition
        );

        if (
            previousSelectedPosition &&
            previousSelectedPosition.isUniquePerGame
        ) {
            newDisabledPositions = newDisabledPositions.filter(
                (pos) => pos !== previousPosition
            );
        }

        if (selectedPosition && selectedPosition.isUniquePerGame) {
            newDisabledPositions.push(newPosition);
        }

        return newDisabledPositions;
    }

    updateMatchPlayers(id, prop, value, swapPlayer, previousPosition) {
        const component = this.props.dataPath.replace(/^\/+/g, "");
        const updateEntity = component.toLowerCase().includes("squad")
            ? "allTeamPlayers"
            : "allMatchPlayers";
        let update = toJS(this.state[updateEntity]);

        if (!update[id]) {
            console.warn(`Player with ID ${id} not found in ${updateEntity}. Skipping update.`);
            return false;
        }

        const teamId = update[id].team;

        if (prop === "isStartingPlayer" && swapPlayer) {
            this.swapPlayers(update, id, swapPlayer, prop, value);
        } else if (prop === "isCaptain") {
            this.selectionStore.items.forEach((item) => {
                if (item.id !== id) {
                    item[prop] = false;
                }
            });

            update = this.markPlayersAsChanged(update, teamId, prop, value, id);
        } else if (prop === "isCheckbox") {
            this.selectionStore.items.forEach((item) => {
                if (item.id !== id) {
                    if (item.position === value) {
                        item.position = 0;
                    }
                } else {
                    item.position = value;
                }
            });

            Object.values(update).forEach((player) => {
                if (
                    player.team === teamId &&
                    player.position === value &&
                    player.id !== id
                ) {
                    update[player.contact] = {
                        ...update[player.contact],
                        position: 0,
                        isChanged: true,
                    };
                }
            });

            update[id] = {
                ...update[id],
                position: value,
                isChanged: true,
            };
        } else if (prop === "shirtNumber") {
            if (previousPosition !== value) {
                update[id] = {
                    ...update[id],
                    number: value,
                    isChanged: true,
                };
            } else {
                return false;
            }
        } else if (prop === "positions") {
            const newPositions = value.map((positionId) => {
                const positionObject = this.props.data.positions.find(
                    (pos) => pos.id === positionId
                );

                return positionObject;
            });

            const selectedItem = this.findSelectedItem(id);

            selectedItem[prop] = newPositions;

            update[id] = { ...update[id], [prop]: value, isChanged: true };
        } else {
            update[id] = { ...update[id], [prop]: value, isChanged: true };
        }

        return update;
    }

    swapPlayers(update, id, swapPlayer, prop, value) {
        Object.values(update).forEach((player) => {
            if (player.contact === id || player.contact === swapPlayer) {
                update[player.contact] = {
                    ...update[player.contact],
                    [prop]: player.contact === id ? !value : value,
                    isChanged: true,
                };
            }
        });

        const items = this.selectionStore.items;
        const indexId = items.findIndex((item) => item.id === id);
        const indexSwap = items.findIndex((item) => item.id === swapPlayer);

        [items[indexId], items[indexSwap]] = [items[indexSwap], items[indexId]];
        items[indexId].isStartingPlayer = value;
        items[indexSwap].isStartingPlayer = !value;

        this.selectionStore.items = [...items];
    }

    handleRoleChange(id, prop, value) {
        const updateEntity =
            prop === "role" ? "allMatchStaff" : "umpiresObject";
        const updateFlag = prop === "role" ? "isStaffChange" : "isUmpireChange";

        const roleList =
            prop === "role"
                ? this.state.data.teamRoleList
                : this.state.data.umpireRoleList;
        const roleId = Object.values(roleList).indexOf(value) + 1;

        let update = toJS(this.state.data[updateEntity]);

        const selectedItem = this.findSelectedItem(id);

        selectedItem[prop] = roleId;

        if (update[id]?.role !== roleId) {
            update[id] = {
                ...update[id],
                ...(prop === "role" ? { matchRole: roleId } : { role: roleId }),
                isChanged: true,
            };

            return {
                ...this.state.data,
                [updateEntity]: update,
                [updateFlag]: true,
            };
        }

        return null;
    }

    calculateUmpireCost(id) {
        const inputElements = Array.from(
            document.querySelectorAll(`.js-calculation-input-${id}`)
        );
        let total = "";
        if (inputElements.length) {
            const inputValues = inputElements.map(
                (el) => parseFloat(el.value) || 0
            );
            total = inputValues.reduce(
                (acc, currentValue) => acc + currentValue,
                0
            );
            document
                .querySelector(`.js-submit-btn-${id}`)
                .removeAttribute("disabled");
        }
        return parseFloat(total).toFixed(2);
    }

    markPlayersAsChanged(update, teamId, prop, value, id) {
        Object.values(update).forEach((player) => {
            if (player.team === teamId && player[prop]) {
                update[player.contact] = {
                    ...update[player.contact],
                    [prop]: false,
                    isChanged: true,
                };
            }
        });
        update[id] = { ...update[id], [prop]: value, isChanged: true };
        return update;
    }
}
